import { cssTransition, toast } from 'react-toastify';

import PropTypes from 'prop-types';
import * as S from './toast.styles';
import Icon from '../icon/Icon';
import H from '../texts/h/H';
import P from '../texts/p/P';

const Fade = cssTransition({
  enter: 'Toastify__fade-enter',
  exit: 'Toastify__fade-exit',
});

const states = {
  info: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'info_circle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
  success: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'check_circle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
  warning: {
    text: { variant: 'neutral', modifier: 'strong' },
    icon: {
      name: 'error_circle_outline',
      variant: 'neutral',
      modifier: 'strong',
    },
  },
  danger: {
    text: { variant: 'neutral', modifier: 'inverted' },
    icon: {
      name: 'warning_triangle_outline',
      variant: 'neutral',
      modifier: 'inverted',
    },
  },
};

const renderIcon = ({ variant }) => (
  <Icon
    name={states[variant].icon.name}
    variant={states[variant].icon.variant}
    modifier={states[variant].icon.modifier}
    width={32}
    height={32}
  />
);

const renderToast = ({ title, description, variant }) => (
  <S.ToastBody>
    <div className='toast-body__icon'>{renderIcon({ variant: variant })}</div>
    <div className='toast-body__text'>
      {title && (
        <H
          variant={states[variant].text.variant}
          modifier={states[variant].text.modifier}
          type='6'
          withMargin={false}
        >
          {title}
        </H>
      )}
      {description && (
        <P
          variant={states[variant].text.variant}
          modifier={states[variant].text.modifier}
          type='2'
          weight='regular'
          withMargin={false}
        >
          {description}
        </P>
      )}
    </div>
  </S.ToastBody>
);

const getContainerId = (id) => id ?? 'default-toast';

const useToast = {
  error: ({ title, id, description, closeButton, autoClose, toastId }) => {
    toast.error(renderToast({ title, description, variant: 'danger' }), {
      containerId: getContainerId(id),
      closeButton: closeButton && (({ closeToast }) => <CloseButton closeToast={closeToast} variant='danger' />),
      autoClose,
      toastId,
    });
  },
  success: ({ title, id, description, closeButton, autoClose, toastId }) => {
    toast.success(renderToast({ title, description, variant: 'success' }), {
      containerId: getContainerId(id),
      closeButton: closeButton && (({ closeToast }) => <CloseButton closeToast={closeToast} variant='success' />),
      autoClose,
      toastId,
    });
  },
  inProgress: ({ title, id, description, closeButton, autoClose, toastId }) => {
    toast.info(renderToast({ title, description, variant: 'info' }), {
      containerId: getContainerId(id),
      closeButton: closeButton && (({ closeToast }) => <CloseButton closeToast={closeToast} variant='info' />),
      autoClose,
      toastId,
    });
  },
  info: ({ title, id, description, closeButton, autoClose, toastId }) => {
    toast.info(renderToast({ title, description, variant: 'info' }), {
      containerId: getContainerId(id),
      closeButton: closeButton && (({ closeToast }) => <CloseButton closeToast={closeToast} variant='info' />),
      autoClose,
      toastId,
    });
  },
  warning: ({ title, id, description, closeButton, autoClose, toastId }) => {
    toast.warning(renderToast({ title, description, variant: 'warning' }), {
      containerId: getContainerId(id),
      closeButton: closeButton && (({ closeToast }) => <CloseButton closeToast={closeToast} variant='warning' />),
      autoClose,
      toastId,
    });
  },
  dismiss: () => {
    toast.dismiss();
  },
};

const CloseButton = ({ closeToast, variant }) => {
  const handleClose = () => {
    try {
      if (typeof closeToast === 'function') {
        closeToast();
      }
    } catch (_) {
      // Evita que deleteToast (react-toastify) rompa la app si la ref del toast es undefined
    }
  };
  return (
    <S.IconCloseWrapper>
      <Icon
        name='cancel_outline'
        variant={states[variant]?.icon?.variant ?? 'neutral'}
        modifier={states[variant]?.icon?.modifier ?? 'inverted'}
        cursor='pointer'
        onClick={handleClose}
        width={24}
        height={24}
      />
    </S.IconCloseWrapper>
  );
};

const Toast = ({ position, autoClose, className, id, fullWidth }) => {
  const containerId = id ?? 'default-toast';
  return (
    <S.Toast
      containerId={containerId}
      position={position}
      autoClose={autoClose}
      hideProgressBar
      newestOnTop
      closeOnClick={false}
      rtl={false}
      pauseOnFocusLoss={false}
      draggable={false}
      pauseOnHover={false}
      transition={Fade}
      className={`${className} ${fullWidth && 'Toast__toast-body--md-full'}`}
      limit={3}
      closeButton={false}
    />
  );
};

Toast.defaultProps = {
  position: 'top-right',
  fullWidth: false,
  autoClose: 5000,
};

Toast.propTypes = {
  position: PropTypes.oneOf([
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ]),
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  id: PropTypes.string,
};

export { Toast, useToast };
